﻿import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button } from '@fluentui/react-components';
import { AddCircleRegular, DeleteRegular, EditRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStores } from '../../../common/hooks';
import { AwardGroup, AwardGroupRef } from '../../../models';
import { FlexBox } from '../../shared-components/flex-box';
import './AwardGroupList.scss';

interface AwardGroupListProps {
  onAwardSelected(award: AwardGroupRef): void;
  onCreateGroup(): void;
  onEditGroup(awardGroup: AwardGroup): void;
  onDeleteGroup(awardGroup: AwardGroup): void;
  onCreateAward(): void;
  onRestoreAward(award: AwardGroupRef): void;
  onEditAward(award: AwardGroupRef): void;
  onDeleteAward(award: AwardGroupRef): void;
}

const AwardGroupListImpl: React.FC<AwardGroupListProps> = (props) => {
  const {
    onAwardSelected,
    onCreateGroup,
    onEditGroup,
    onDeleteGroup,
    onCreateAward,
    onRestoreAward,
    onEditAward,
    onDeleteAward,
  } = props;

  const {
    manageAwardListPageUiStore,
  } = useStores();

  return (
    <FlexBox
      direction='column'
      className='award-group-list'
    >
      <Accordion
        collapsible
        className='award-group-list__accordion'
      >
        {
          manageAwardListPageUiStore.awardGroups.map((awardGroup) => {
            const isGroupDeleted = manageAwardListPageUiStore.deletedGroupIds.has(awardGroup.awardGroupId);

            return (
              <AccordionItem
                key={`award-group-list-${awardGroup.awardGroupId}`}
                value={awardGroup.awardGroupId}
              >
                <FlexBox
                  className='list-wrapper'
                  aligns='center'
                >
                  <AccordionHeader>{awardGroup.title}</AccordionHeader>
                  <FlexBox className='list-wrapper__actions'>
                    {
                      !isGroupDeleted &&
                      <Button
                        appearance='subtle'
                        title='Edit'
                        onClick={(event) => {
                          event.preventDefault();
                          onEditGroup(awardGroup);
                        }}
                        icon={<EditRegular />}
                      />
                    }
                    {
                      awardGroup.awards.length === 0 && !isGroupDeleted &&
                      <Button
                        appearance='subtle'
                        title='Delete'
                        onClick={() => onDeleteGroup(awardGroup)}
                        icon={<DeleteRegular />}
                      />
                    }
                  </FlexBox>
                </FlexBox>
                <AccordionPanel>
                  {
                    awardGroup.awards.map((award) => {
                      const isAwardDeleted = manageAwardListPageUiStore.deletedAwardIds.has(award.awardId);
                      const isAwardUpdated = manageAwardListPageUiStore.updatedAwardMap.has(award.awardId);

                      return (
                        <FlexBox
                          key={award.awardId}
                          className='list-wrapper'
                          aligns='center'
                        >
                          <button
                            onClick={() => onAwardSelected(award)}
                            key={award.awardId.toString()}
                            className={`panel__item ${isAwardDeleted ? 'panel__item--deleted' : ''}`}
                          >
                            {
                              isAwardUpdated &&
                              <div className='dot' />
                            }
                            {award.title}
                          </button>
                          <FlexBox className='list-wrapper__actions'>
                            {
                              isAwardDeleted &&
                              <Button
                                appearance='subtle'
                                title='Add'
                                onClick={() => onRestoreAward(award)}
                                icon={<AddCircleRegular />}
                              />
                            }
                            {
                              !isAwardDeleted &&
                              <Button
                                appearance='subtle'
                                title='Edit'
                                onClick={() => onEditAward(award)}
                                icon={<EditRegular />}
                              />
                            }
                            {
                              !isAwardDeleted &&
                              <Button
                                appearance='subtle'
                                title='Delete'
                                onClick={() => onDeleteAward(award)}
                                icon={<DeleteRegular />}
                              />
                            }
                          </FlexBox>
                        </FlexBox>
                      )
                    })
                  }
                </AccordionPanel>
              </AccordionItem>
            );
          })
        }
      </Accordion>
      <FlexBox
        isFullWidth
        className='award-group-list__actions'
      >
        <Button
          appearance='secondary'
          onClick={() => onCreateAward()}
        >
          Add award
        </Button>
        <Button
          appearance='secondary'
          onClick={() => onCreateGroup()}
        >
          Create New Group
        </Button>
      </FlexBox>
    </FlexBox>
  );
}

export const AwardGroupList = observer(AwardGroupListImpl);
